import { usePatchProfilePasswordMutation } from '@/api/serverApi'
import { Button, InputLabel, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'

const editPasswordFormSchema = v.object({
  current_password: v.string([v.minLength(1, 'Password is required')]),
  new_password: v.string([v.minLength(1, 'Password is required')]),
  new_password_confirmation: v.string([v.minLength(1, 'Password confirmation is required')]),
})

type EditPasswordFormInputs = v.Input<typeof editPasswordFormSchema>

function EditPasswordForm () {
  const textSpec = {
    title: 'Password',
    subtitle: 'Confirm your current password to set a new one',
    currentPasswordField: {
      label: 'Current Password',
      placeholder: 'Current Password',
    },
    newPasswordField: {
      label: 'New Password',
      placeholder: 'New Password',
    },
    newPasswordConfirmationField: {
      label: 'Confirm New Password',
      placeholder: 'Confirm New Password',
    },
    submitButtonLabel: 'Save Changes',
    successToastMessage: {
      title: 'Details changed successfully',
      body: 'Your password has now been changed.',
    },
    errorToastMessage: {
      title: 'Something went wrong',
      body: 'Please try again later.',
    },
  }

  const errorHandler = useErrorHandler()

  const { register, handleSubmit, formState: { errors }, setError, reset } = useForm<EditPasswordFormInputs>({
    resolver: valibotResolver(editPasswordFormSchema),
  })

  const [update, { isLoading: isSubmitting }] = usePatchProfilePasswordMutation()

  const onSubmit: SubmitHandler<EditPasswordFormInputs> = (data) => {
    update({ body: data })
      .unwrap()
      .then((_) => {
        toastHelper.success(textSpec.successToastMessage.title, textSpec.successToastMessage.body)
        reset()
      })
      .catch((error) => {
        if (error?.status === 422) {
          errorHandler(error, setError)
        } else {
          toastHelper.error(textSpec.errorToastMessage.title, error.data?.message ?? textSpec.errorToastMessage.body)
        }
      })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-start w-full max-w-[615px]"
    >
      <h2 className="mb-[5px] text-lg leading-[21.6px] font-semibold">{textSpec.title}</h2>

      <h3 className="mb-[25px] text-sm leading-[19.6px] font-medium">{textSpec.subtitle}</h3>

      <div className="mb-[30px] w-full">
        <InputLabel name="name">{textSpec.currentPasswordField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.currentPasswordField.placeholder}
          error={errors?.current_password?.message}
          type="password"
          {...register('current_password')}
        />
      </div>

      <div className="mb-[30px] w-full">
        <InputLabel name="name">{textSpec.newPasswordField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.newPasswordField.placeholder}
          error={errors?.new_password?.message}
          type="password"
          {...register('new_password')}
        />
      </div>

      <div className="mb-[30px] w-full">
        <InputLabel name="email">{textSpec.newPasswordConfirmationField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.newPasswordConfirmationField.placeholder}
          error={errors?.new_password_confirmation?.message}
          type="password"
          {...register('new_password_confirmation')}
        />
      </div>

      <div className="w-full flex justify-end">
        <div className="mt-2">
          <Button
            variant="dark"
            isLoading={isSubmitting}
            type="submit"
            isBlock
          >
            {textSpec.submitButtonLabel}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default EditPasswordForm
